import React, { useMemo, useCallback } from 'react'
import { Heading, Flex, Text, Image } from 'vvs-uikit'
import BigNumber from 'bignumber.js'
import tokens from 'config/constants/tokens'
import { BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'

import { useTranslation } from 'contexts/Localization'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import usePriceXvvsUsdc from 'views/xVvs/hooks/usePriceXvvsUsdc'
import { useUserVvsBoost, usePollVvsBoostPoolData } from 'state/xVvs/hooks'

import Balance from 'components/Balance'
import LocalBalance from 'components/LocalBalance'
import { Wrapper } from '../FarmsCard'

const ItemTitle = ({ children }) => (
  <Text textTransform="capitalize" minWidth={['110px', null, null, null]} color="grey" fontSize="13px">
    {children}
  </Text>
)
const ItemValue = ({ children }) => (
  <Text minWidth={['110px', null, null, null]} color="white" bold fontSize="20px">
    {children}
  </Text>
)

const XvvsCard = () => {
  const { t } = useTranslation()
  const vaultsApr = useVaultsApr()
  const { boostData } = useUserVvsBoost()
  const xvvsPriceUsd = usePriceXvvsUsdc()

  usePollVvsBoostPoolData()
  const activeStakes = boostData.stakes.filter((x) => x.active)
  const totalStaked = activeStakes.reduce((acc, cur) => acc.plus(cur.amount), BIG_ZERO)

  const sumAmountInPool = useCallback(
    (poolId: number): BigNumber =>
      activeStakes
        .filter((x) => x.poolId === poolId)
        .map((x) => x.amount)
        .reduce((acc, cur) => acc.plus(cur), BIG_ZERO),
    [activeStakes],
  )

  const balance = useMemo(() => {
    const stakedAmountInUsd = totalStaked.times(xvvsPriceUsd)
    return getBalanceAmount(stakedAmountInUsd, tokens.xvvs.decimals)
  }, [totalStaked, xvvsPriceUsd])

  const averageApr = useMemo(
    () =>
      vaultsApr
        .map(({ poolId, apr }) => sumAmountInPool(poolId).div(totalStaked).times(apr))
        .reduce((acc, cur) => acc.plus(cur), BIG_ZERO),
    [vaultsApr, totalStaked, sumAmountInPool],
  )

  return (
    <Wrapper mt={[null, null, null, '36px']} mb={['24px', null, null, '36px']} flexDirection="column">
      <Flex alignItems="center" justifyContent="flex-start">
        <Image src="/images/dashboard/vault-icon.svg" width={32} height={32} mr="6px" />
        <Heading color="primary"> {t('xVVS Vaults')} </Heading>
      </Flex>

      <Flex className="info" justifyContent={['space-between', null, null, 'flex-start']}>
        <Flex flexDirection="column" mt="10px" mr="24px">
          <ItemValue>
            <LocalBalance
              bold
              color="white"
              fontSize="20px"
              lineHeight="1.1"
              value={balance}
              decimals={balance.gt(0) ? 2 : 0}
            />
          </ItemValue>
          <ItemTitle> {t('Total staked')} </ItemTitle>
        </Flex>
        <Flex flexDirection="column" mt="10px">
          <ItemValue>
            <Balance
              bold
              color="white"
              fontSize="20px"
              lineHeight="1.1"
              unit="%"
              value={averageApr.gt(0) ? averageApr.toNumber() : 0}
              decimals={averageApr.gt(0) ? 2 : 0}
            />
          </ItemValue>
          <ItemTitle>{t('Average APR')}</ItemTitle>
        </Flex>
      </Flex>
    </Wrapper>
  )
}

export default XvvsCard
